/**
 * CoinGlass TanStack Query Hooks
 * One hook per API endpoint with appropriate refetch intervals
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Asset } from '@/stores/alert-store';
import type {
  OverviewResponse,
  OIBreakdownResponse,
  TakerFlowResponse,
  WhalePositionsResponse,
  OptionsResponse,
  PositioningResponse,
  ETFFlowsResponse,
  CoinbasePremiumResponse,
} from '@/types/coinglass';

export function useCoinglassOverview(asset: Asset) {
  return useQuery({
    queryKey: ['coinglass', 'overview', asset],
    queryFn: () => api<OverviewResponse>(`/api/coinglass/${asset}/overview`),
    refetchInterval: 60_000,
  });
}

export function useCoinglassOIBreakdown(asset: Asset) {
  return useQuery({
    queryKey: ['coinglass', 'oi-breakdown', asset],
    queryFn: () => api<OIBreakdownResponse>(`/api/coinglass/${asset}/oi-breakdown`),
    refetchInterval: 60_000,
  });
}

export function useCoinglassTakerFlow(asset: Asset) {
  return useQuery({
    queryKey: ['coinglass', 'taker-flow', asset],
    queryFn: () => api<TakerFlowResponse>(`/api/coinglass/${asset}/taker-flow`),
    refetchInterval: 60_000,
  });
}

export function useCoinglassWhalePositions(asset: Asset) {
  return useQuery({
    queryKey: ['coinglass', 'whale-positions', asset],
    queryFn: () => api<WhalePositionsResponse>(`/api/coinglass/${asset}/whale-positions`),
    refetchInterval: 60_000,
  });
}

export function useCoinglassOptions(asset: Asset) {
  return useQuery({
    queryKey: ['coinglass', 'options', asset],
    queryFn: () => api<OptionsResponse>(`/api/coinglass/${asset}/options`),
    refetchInterval: 5 * 60_000,
    enabled: asset === 'BTC' || asset === 'ETH',
  });
}

export function useCoinglassPositioning(asset: Asset) {
  return useQuery({
    queryKey: ['coinglass', 'positioning', asset],
    queryFn: () => api<PositioningResponse>(`/api/coinglass/${asset}/positioning`),
    refetchInterval: 60_000,
  });
}

export function useCoinglassETFFlows() {
  return useQuery({
    queryKey: ['coinglass', 'etf-flows'],
    queryFn: () => api<ETFFlowsResponse>('/api/coinglass/etf-flows'),
    refetchInterval: 5 * 60_000,
  });
}

export function useCoinglassCBPremium() {
  return useQuery({
    queryKey: ['coinglass', 'coinbase-premium'],
    queryFn: () => api<CoinbasePremiumResponse>('/api/coinglass/coinbase-premium'),
    refetchInterval: 60_000,
  });
}
